import React from 'react';
import {
  AppShell,
  Burger,
  Group,
  Text,
  UnstyledButton,
  Avatar,
  Menu,
  rem,
  ActionIcon,
  Title,
  Badge,
  Box,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconSettings,
  IconLogout,
  IconUser,
  IconBell,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';
import { AdminSidebar } from './AdminSidebar';
import { useAuth, useUserProfile } from '../../hooks/useAuth';
import { useBadgeCount } from '../../hooks/useBadgeCounts';
import { AdminErrorBoundary } from '../common/AdminErrorBoundary';
import type { AdminLayoutProps } from '../../types/navigation';

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [opened, { toggle }] = useDisclosure();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { logout } = useAuth();
  const { getDisplayName, getInitials, role } = useUserProfile();
  const unreadCount = useBadgeCount('unread');

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (_error) {
      // Logout failed
    }
  };

  const toggleTheme = () => {
    toggleColorScheme();
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 280, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          {/* Left side - Logo and burger */}
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Group gap="xs">
              <Title
                order={3}
                style={{
                  color: 'var(--mantine-color-admin-6)',
                  fontWeight: 700,
                }}
              >
                YochienAI
              </Title>
              <Text size="sm" c="dimmed">
                Admin Portal
              </Text>
            </Group>
          </Group>

          {/* Right side - Actions and user menu */}
          <Group gap="sm">
            {/* Theme toggle */}
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={toggleTheme}
              title={colorScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {colorScheme === 'dark' ? (
                <IconSun style={{ width: rem(20), height: rem(20) }} />
              ) : (
                <IconMoon style={{ width: rem(20), height: rem(20) }} />
              )}
            </ActionIcon>

            {/* Notifications */}
            <ActionIcon
              variant="subtle"
              size="lg"
              title="Notifications"
            >
              <Box pos="relative">
                <IconBell style={{ width: rem(20), height: rem(20) }} />
                {unreadCount > 0 && (
                  <Badge
                    size="xs"
                    color="red"
                    variant="filled"
                    pos="absolute"
                    top={-5}
                    right={-5}
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Box>
            </ActionIcon>

            {/* User menu */}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton
                  style={{
                    padding: rem(8),
                    borderRadius: rem(8),
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(8),
                    transition: 'background-color 0.2s ease',
                  }}
                  className="admin-user-menu-trigger"
                >
                  <Avatar size="sm" radius="xl">
                    {getInitials()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>
                      {getDisplayName()}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {role?.label}
                    </Text>
                  </div>
                  <IconChevronDown style={{ width: rem(14), height: rem(14) }} />
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  leftSection={<IconUser style={{ width: rem(14), height: rem(14) }} />}
                >
                  Profile
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconSettings style={{ width: rem(14), height: rem(14) }} />}
                >
                  Settings
                </Menu.Item>

                <Menu.Divider />

                <Menu.Item
                  color="red"
                  leftSection={<IconLogout style={{ width: rem(14), height: rem(14) }} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Sidebar */}
      <AppShell.Navbar p="md">
        <AdminSidebar />
      </AppShell.Navbar>

      {/* Main content */}
      <AppShell.Main>
        <AdminErrorBoundary>
          <div className="admin-content">
            {children}
          </div>
        </AdminErrorBoundary>
      </AppShell.Main>

    </AppShell>
  );
};

export default AdminLayout;
